import {Option, Tabs} from 'platform/components';

import {ComponentProps} from 'react';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DocumentTemplateOptionGroup} from '@dms/settings/shared';

import {Nullish} from 'shared/web';

import {AccountingTab} from '../../(sections)/AccountingTab/AccountingTab';
import {DepositTab} from '../../(sections)/DepositTab/DepositTab';
import {DocumentsTab} from '../../(sections)/DocumentTab/DocumentsTab';
import {GeneralTab} from '../../(sections)/GeneralTab/GeneralTab';
import {PaymentTab} from '../../(sections)/PaymentTab/PaymentTab';
import {ReportingTab} from '../../(sections)/ReportingTab/ReportingTab';
import {WarehousesTab} from '../../(sections)/WarehousesTab/WarehousesTab';

type GeneralTabProps = ComponentProps<typeof GeneralTab>;

interface WarehouseDirectSaleVariantsDetailTabsProps extends Pick<
  GeneralTabProps,
  | 'control'
  | 'formApi'
  | 'isFetchingAuthorizationProfile'
  | 'onDiscard'
  | 'onAuthorizationProfileChange'
  | 'directSaleVariantId'
  | 'authorizationProfiles'
  | 'branchList'
  | 'directSaleTypes'
  | 'workRateTypes'
  | 'materialPriceTypes'
> {
  activeTabId: string | Nullish;
  onTabChange: (tabId: string) => void;
  isTemplatesLoading: boolean;
  standardInvoiceTemplateOptions: DocumentTemplateOptionGroup[];
  internalInvoiceTemplateOptions: DocumentTemplateOptionGroup[];
  directSaleDocSeriesOptions: Option[];
  offerDocSeriesOptions: Option[];
  fastTrackDocSeriesOptions: Option[];
  invoiceDocSeriesOptions: Option[];
}

export function WarehouseDirectSaleVariantsDetailTabs(
  props: WarehouseDirectSaleVariantsDetailTabsProps
) {
  return (
    <Tabs
      isFullHeight
      variant="condensed"
      activeTabId={props.activeTabId}
      onChange={props.onTabChange}
      tabs={[
        {
          id: 'general',
          title: i18n.t('entity.warehouse.labels.general'),
          content: (
            <GeneralTab
              control={props.control}
              formApi={props.formApi}
              isFetchingAuthorizationProfile={props.isFetchingAuthorizationProfile}
              onDiscard={props.onDiscard}
              onAuthorizationProfileChange={props.onAuthorizationProfileChange}
              directSaleVariantId={props.directSaleVariantId}
              authorizationProfiles={props.authorizationProfiles}
              branchList={props.branchList}
              directSaleTypes={props.directSaleTypes}
              workRateTypes={props.workRateTypes}
              materialPriceTypes={props.materialPriceTypes}
              docSeriesOptions={props.directSaleDocSeriesOptions}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.general')}
            />
          ),
        },
        {
          id: 'documents',
          title: i18n.t('entity.warehouse.labels.documents'),
          content: (
            <DocumentsTab
              control={props.control}
              onDiscard={props.onDiscard}
              directSaleVariantId={props.directSaleVariantId}
              offerDocSeriesOptions={props.offerDocSeriesOptions}
              fastTrackDocSeriesOptions={props.fastTrackDocSeriesOptions}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.documents')}
            />
          ),
        },
        {
          id: 'payment',
          title: i18n.t('entity.warehouse.labels.paymentType'),
          content: (
            <PaymentTab
              control={props.control}
              formApi={props.formApi}
              onDiscard={props.onDiscard}
              directSaleVariantId={props.directSaleVariantId}
              standardInvoiceTemplateOptions={props.standardInvoiceTemplateOptions}
              internalInvoiceTemplateOptions={props.internalInvoiceTemplateOptions}
              invoiceDocSeriesOptions={props.invoiceDocSeriesOptions}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.payment')}
            />
          ),
        },
        {
          id: 'deposit',
          title: i18n.t('entity.order.labels.deposit'),
          content: (
            <DepositTab
              control={props.control}
              formApi={props.formApi}
              onDiscard={props.onDiscard}
              directSaleVariantId={props.directSaleVariantId}
              docSeriesOptions={props.invoiceDocSeriesOptions}
              invoiceTemplateOptions={props.standardInvoiceTemplateOptions}
              isTemplatesLoading={props.isTemplatesLoading}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.deposit')}
            />
          ),
        },
        {
          id: 'accounting',
          title: i18n.t('entity.warehouse.labels.accounting'),
          content: (
            <AccountingTab
              control={props.control}
              formApi={props.formApi}
              onDiscard={props.onDiscard}
              directSaleVariantId={props.directSaleVariantId}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.accounting')}
            />
          ),
        },
        {
          id: 'reporting',
          title: i18n.t('entity.warehouse.labels.reporting'),
          content: (
            <ReportingTab
              control={props.control}
              formApi={props.formApi}
              onDiscard={props.onDiscard}
              directSaleVariantId={props.directSaleVariantId}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.reporting')}
            />
          ),
        },
        {
          id: 'warehouses',
          title: i18n.t('entity.warehouse.labels.warehouses'),
          content: (
            <WarehousesTab
              control={props.control}
              formApi={props.formApi}
              onDiscard={props.onDiscard}
              directSaleVariantId={props.directSaleVariantId}
              data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('tab.warehouses')}
            />
          ),
        },
      ]}
    />
  );
}
